import { Box, Button, styled, useTheme } from '@mui/material';
import { Multiselect } from '../../../components/Multiselect/Multiselect';
import { useCallback, useMemo, useState, useEffect } from 'react';
import { HierarchicalOption, SelectItem, User } from '../../../types';
import { Table } from '@tanstack/react-table';
import { OpenTimesheetApprovalTableRow } from '../hooks/useAllOPLogsMapped';
import { ChipsSelectorMultiselect } from '../../../components/ButtonSelector/ChipsSelectorMultiselect';
import { HierarchicalDropdownMultiSelect } from '../../../components/DropdownMultiSelect/HierarchicalDropdownMultiSelect';
import { Typography } from '../../../components/Typography/Typography';
import { useAtom } from 'jotai';
import {
  tsApprovalsSelectedOPsFilterState,
  tsApprovalsStatusFilter,
} from '../../../state/UIAccountantState';
import { StatusFilter } from '../../../components/StatusFilter/StatusFilter';
import { roleLabel } from '../../../utils/roleLabels';

interface OpenTimesheetsFilterSectionProps {
  table: Table<OpenTimesheetApprovalTableRow>;
  operatingPartners: User[];
}

export const OpenTimesheetsFilterSection = ({
  table,
  operatingPartners,
}: OpenTimesheetsFilterSectionProps) => {
  const { colors } = useTheme();
  const [selectedOPs, setSelectedOPs] = useAtom(tsApprovalsSelectedOPsFilterState);
  const [statusFilter, setStatusFilter] = useAtom(tsApprovalsStatusFilter);
  const [selectedStatus, setSelectedStatus] = useState<SelectItem[]>([]);
  const [selectedRole, setSelectedRole] = useState<SelectItem[]>([]);

  const sortedOPList = useMemo(
    () => {
      if (!operatingPartners) return [];
      const allOPs = operatingPartners.sort().map((user) => ({ id: user.id, value: user.name }));
      return allOPs;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [table, operatingPartners]
  );

  const validSelectedOPs = useMemo(() => {
    const availableOPIds = new Set(sortedOPList.map(op => op.id));
    return selectedOPs.filter(op => availableOPIds.has(Number(op.id)));
  }, [selectedOPs, sortedOPList]);

  const onChangeOPFilter = useCallback(
    (OPs: SelectItem[] | undefined) => {
      if (OPs) {
        setSelectedOPs(OPs);
        table.getColumn('timesheetName')?.setFilterValue(OPs);
      }
    },
    [table, setSelectedOPs]
  );

  const sortedUniqueStatuses = useMemo(
    () =>
      Array.from(table.getColumn('status')?.getFacetedUniqueValues().keys() ?? [])
        .sort()
        .map((value) => ({ id: value, value })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [table, operatingPartners]
  );

  const onSelectStatus = useCallback(
    (statuses: SelectItem[] | undefined) => {
      if (statuses) {
        setSelectedStatus(statuses);
        table.getColumn('status')?.setFilterValue(statuses);
      }
    },
    [table, setSelectedStatus]
  );

  const hierarchicalRoles: HierarchicalOption[] = useMemo(
    () => {
      const tableData = table.getFilteredRowModel().rows.map((row) => row.original);
      const roleMap = new Map<string, Set<string>>();

      tableData.forEach((row) => {
        if (row.pendingWith?.role && row.pendingWith?.users) {
          const role = row.pendingWith.role;
          if (!roleMap.has(role)) {
            roleMap.set(role, new Set());
          }
          row.pendingWith.users.forEach(user => {
            roleMap.get(role)?.add(user);
          });
        }
      });

      // Group header shows the DISPLAY role label (Operating Partner -> Submitter, OP Manager -> Manager),
      // but the parent id stays the raw role so filtering keeps matching on the underlying value.
      return Array.from(roleMap.entries()).map(([role, usersSet]) => ({
        parent: { id: role, value: roleLabel(role) },
        children: Array.from(usersSet).sort().map(user => ({ id: `${role}-${user}`, value: user }))
      })).sort((a, b) => a.parent.value.localeCompare(b.parent.value));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [table, operatingPartners]
  );

  const onSelectRole = useCallback(
    (selectedUsers: SelectItem[] | undefined) => {
      if (selectedUsers) {
        setSelectedRole(selectedUsers);
        table.getColumn('pendingWith')?.setFilterValue(selectedUsers);
      }
    },
    [table, setSelectedRole]
  );

  useEffect(() => {
    table.getColumn('timesheetName')?.setFilterValue(validSelectedOPs);
  }, [validSelectedOPs, table]);

  const onFiltersReset = useCallback(() => {
    setSelectedOPs([]);
    setSelectedRole([]);
    setSelectedStatus([]);
    table.getColumn('timesheetName')?.setFilterValue([]);
    table.getColumn('pendingWith')?.setFilterValue([]);
    table.getColumn('status')?.setFilterValue([]);
  }, [setSelectedOPs, setSelectedRole, setSelectedStatus, table]);

  const isResetDisabled = useMemo(() => {
    return validSelectedOPs?.length === 0 && selectedRole.length === 0 && selectedStatus.length === 0;
  }, [validSelectedOPs?.length, selectedRole.length, selectedStatus.length]);

  return (
    <Container>
      <Multiselect
        style={{
          width: '200px',
          marginRight: '14px',
        }}
        fieldPlaceholder={'Select submitter'}
        options={sortedOPList as SelectItem[]}
        onChange={(_, OPs) => onChangeOPFilter(OPs)}
        value={validSelectedOPs}
      />
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
        <HierarchicalDropdownMultiSelect
          options={hierarchicalRoles}
          selectedItems={selectedRole}
          onSelect={(users) => onSelectRole(users)}
          placeholder="Select role"
          width="220px"
          subtitle='Pending Roles'
        />
        <StatusFilter value={statusFilter} onChange={(value) => value && setStatusFilter(value)} />
      </Box>
      <ChipsSelectorMultiselect
        options={sortedUniqueStatuses}
        selectedItems={selectedStatus}
        onSelect={(statuses) => onSelectStatus(statuses)}
        chipHeight='36px'
      />
      <Button
        onClick={onFiltersReset}
        variant='text'
        style={{ height: '28px', marginTop: '2px' }}
        disabled={isResetDisabled}
        sx={{
          '.MuiButton-startIcon': { marginRight: '2px' },
          '&.Mui-disabled': { opacity: 0.4, cursor: 'not-allowed', pointerEvents: 'auto' },
        }}
      >
        <Typography variant='subtitle1' color={colors.textAccent.default}>
          Reset Filters
        </Typography>
      </Button>
    </Container>
  );
};

const Container = styled('div')`
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  gap: 24px;
`;
